import {Language} from "@variocube/app-api";
import {ArrayUtils, defined, StringUtils} from "@variocube/app-common";
import {
    Avatar,
    BackButton,
    classNames,
    EnglishKeyboardLayout,
    GermanKeyboardLayout,
    IconNames,
    Keyboard,
    KeyPad,
    layout,
    List,
    ListItem,
    ListItemDescription,
    ListItemIcon,
    ListItemName,
    ListItemText,
    MaterialIcon,
    TextInput,
    typography,
    useAsync,
    useTypeRef,
    View,
    ViewContent,
    ViewDescription,
    ViewFooter,
    ViewFooterItem,
    ViewHeader,
    ViewHeaderItem,
    ViewTitle
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {style, stylesheet} from "typestyle";
import {commonStyles} from "./commonStyles";
import {Account, Capability, compareAccounts, PostBoxIdDigits, PostBoxIdLength, RecipientSelection} from "./model";
import {RecipientIndex} from "./RecipientIndex";
import {useLocalization} from "../i18n";

export interface RecipientSelectionViewProps {
    onBack: () => void;
    onSelect: (recipient: Account) => void;
    recipientSelection: RecipientSelection;
    accounts: Account[];
    poBoxUrl?: string;
    title?: string;
    description?:string;
}


export function RecipientSelectionView(props: RecipientSelectionViewProps) {
    switch (props.recipientSelection) {
        case RecipientSelection.Search:
        case RecipientSelection.List:
            return <RecipientSelectionListView {...props} />;

        case RecipientSelection.None:
        default:
            return <RecipientSelectionPostBoxIdView {...props} />;
    }
}

function RecipientSelectionListView({onBack, onSelect, recipientSelection, accounts, title, description}: RecipientSelectionViewProps) {
    const {t, language} = useLocalization();

    const [keyboardOpen, setKeyboardOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [hideKeyboardTimeout, setHideKeyboardTimeout] = useState<number>();
    const [type, setType] = useTypeRef();

    const recipients = useMemo(() => accounts.filter(account => account.capabilities && account.capabilities.indexOf(Capability.InboundMail) >= 0), [accounts]);

    const index = useMemo(() => recipients && new RecipientIndex(recipients), [recipients]);

    const list = useMemo(() => {
        if (query && index) {
            return index.query(query).map(result => result.key);
        }
        if (recipientSelection == RecipientSelection.List) {
            return recipients.sort(compareAccounts);
        }
    }, [query, index, recipientSelection, recipients]);

    const handleQueryFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        clearTimeout(hideKeyboardTimeout);
        setKeyboardOpen(true);
    }, [hideKeyboardTimeout]);

    const handleQueryBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        setHideKeyboardTimeout(window.setTimeout(() => setKeyboardOpen(false), 250));
    }, []);

    return (
        <View>
            <ViewHeader className={commonStyles.viewHeader}>

                <ViewHeaderItem>
                    <BackButton onClick={onBack} label={t("Back")}/>
                </ViewHeaderItem>

                <ViewHeaderItem fill>
                    <ViewTitle>{title??t("SelectRecipientTitle")}</ViewTitle>
                    <ViewDescription>{description??t("SelectRecipientDescription")}</ViewDescription>
                </ViewHeaderItem>

                <ViewHeaderItem className={classNames(styles.queryInputContainer)}>
                    <div className={styles.searchIconContainer}>
                        <MaterialIcon icon={IconNames.search} className={styles.searchIcon}/>
                    </div>
                    <TextInput
                        value={query}
                        onChange={setQuery}
                        placeholder={t("Search")}
                        typeRef={setType}
                        onFocus={handleQueryFocus}
                        onBlur={handleQueryBlur}
                        outline
                        className={styles.searchInput}
                    />
                </ViewHeaderItem>

            </ViewHeader>

            <ViewContent className={commonStyles.selectionViewContent}>
                {list ? (
                    <List>
                        {list.map(recipient => (
                            <RecipientItem
                                key={recipient.postBoxId}
                                recipient={recipient}
                                onSelect={onSelect}
                            />
                        ))}
                    </List>
                ) : (
                    <div className={styles.useSearchMessage}>
                        {t("UseSearchMessage")}
                    </div>
                )}
            </ViewContent>

            <ViewFooter>
                <ViewFooterItem
                    className={classNames(styles.keyboardExpander, (!list || keyboardOpen) && styles.keyboardExpanderOpen)}
                    fill>
                    <Keyboard
                        className={styles.keyboard}
                        onType={type}
                        layout={language == Language.DE ? GermanKeyboardLayout : EnglishKeyboardLayout}
                    />
                </ViewFooterItem>
            </ViewFooter>
        </View>
    );
}


interface RecipientItemProps {
    recipient: Account;
    onSelect: (recipient: Account) => void;
}
function RecipientItem({recipient, onSelect}: RecipientItemProps) {
    const handleClick = useCallback(() => onSelect(recipient), [onSelect, recipient]);
    const abbr = recipient.displayAbbr || StringUtils.abbreviateInitials(recipient.displayName, 3);
    return (
        <ListItem onClick={handleClick}>
            <ListItemIcon>
                <Avatar primary>{abbr}</Avatar>
            </ListItemIcon>
            <ListItemText>
                <ListItemName>{recipient.displayName}</ListItemName>
                <ListItemDescription>{recipient.displayDescription}</ListItemDescription>
            </ListItemText>
            <ListItemIcon>
                {MaterialIcon.chevron_right}
            </ListItemIcon>
        </ListItem>
    );
}


function RecipientSelectionPostBoxIdView({onBack, onSelect, accounts, poBoxUrl}: RecipientSelectionViewProps) {
    //const strings = useStrings(resources);
    const {t} = useLocalization();

    const [value, setValue] = useState("");

    const {loading, error} = useAsync(async () => {
        if (value.length == PostBoxIdLength) {
            const account = accounts?.find(account => account.postBoxId == value)
                ?? (defined(poBoxUrl) && await fetchPostBox(poBoxUrl, value));

            if (account) {
                onSelect(account);
                return;
            }
            throw new Error("Invalid postbox.");
        }
    }, [value, accounts, poBoxUrl, onSelect]);

    const digits = useMemo(() => ArrayUtils.splitIntoChunks(PostBoxIdDigits, 4), []);

    const feedback = useMemo(() => {
        if (error) {
            return {
                message: t("InvalidPostBoxId"),
                "class": typography.colorError,
            };
        }
        else if (loading) {
            return {
                message: t("CheckingPostBoxId"),
                "class": undefined,
            };
        }
        else if (value.length > 0 && value.length < PostBoxIdLength) {
            return {
                message: t("MoreDigitsRequired"),
                "class": typography.muted,
            };
        }
        else {
            return {
                message: t("PleaseEnterPostBoxId"),
                "class": typography.muted,
            };
        }
    }, [value, loading, error]);

    const handleType = useCallback((digit: string) => setValue(value => value + digit), []);
    const handleChange = useCallback((value: string) => setValue(value), []);

    return (
        <View>
            <ViewHeader className={commonStyles.viewHeader}>

                <ViewHeaderItem>
                    <BackButton onClick={onBack} label={t("Back")}/>
                </ViewHeaderItem>

                <ViewHeaderItem fill>
                    <ViewTitle>{t("EnterPostBoxTitle")}</ViewTitle>
                    <ViewDescription>{t("EnterPostBoxDescription")}</ViewDescription>
                </ViewHeaderItem>

            </ViewHeader>

            <ViewContent
                className={classNames(layout.horizontal, layout.spaceEvenly, commonStyles.selectionViewContent)}>

                <div
                    className={classNames(layout.vertical, layout.center, typography.alignCenter, postBoxIdInputWidth)}>

                    <TextInput
                        value={value}
                        onChange={handleChange}
                        type={"text"}
                        disabled={loading}
                        outline
                    />

                    <p className={feedback.class}>{feedback.message}</p>

                </div>

                <div className={classNames(layout.vertical, layout.center)}>
                    <KeyPad
                        primary
                        round
                        size={2}
                        onType={handleType}
                        digits={digits}
                        disabled={loading}
                    />
                </div>

            </ViewContent>
        </View>
    );
}

async function fetchPostBox(poBoxUrl: string, value: string) {
    const response = await window.fetch(`${poBoxUrl}?postBoxId=${value}`);
    if (response.ok) {
        const accounts = await response.json() as Account[];
        return accounts.pop();
    }
    throw new Error('postbox query failed');
}

const postBoxIdInputWidth = style({
    width: "20em",
});

const styles = stylesheet({

    queryInputContainer: {
        position: "relative",
        marginRight: "1em",
        width: "20em",
    },

    searchIconContainer: {
        position: "absolute",
        top: "50%",
        left: "0.5em",
        transform: "translateY(-50%)",
        display: "flex",
    },
    searchIcon: {
        fontSize: "1.5em"
    },
    searchInput: {
        fontSize: "1.5em"
    },

    useSearchMessage: {
        padding: "2em 0",
        textAlign: "center",
        maxWidth: "18em",
        margin: "0 auto",
        fontSize: "1.25em",
        color: "#666"
    },

    keyboard: {
        padding: "0.25em 0",
    },
    keyboardExpander: {
        display: "none",
        maxHeight: 0,
        transition: "maxHeight 0.5s",
    },
    keyboardExpanderOpen: {
        display: "block",
        maxHeight: "100%"
    }

});
